import {
  Dialog,
  DialogTitle,
  DialogContent,
  Avatar,
  Box,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Button
} from "@mui/material"
import {
  Public,
  Lock,
  MoreVert,
  AdminPanelSettings,
  PersonRemove,
  SupervisorAccount,
  ExitToApp,
  WhatsApp
} from "@mui/icons-material"
import { formatDistanceToNow } from "date-fns"
import { es } from 'date-fns/locale'
import { useState } from "react"
import { ChannelDto } from "../../../types/channel"
import { ProfileDto } from "../../../types/UserToken"
import { ChannelMemberDto } from "../../../types/channelMember"

interface Props {
  open: boolean
  onClose: () => void
  channel: ChannelDto | null
  members: ChannelMemberDto[]
  currentUser: ProfileDto
  activeUserIds: number[]
  onPromote: (member: ChannelMemberDto, role: "Admin" | "Member") => void
  onRemove: (member: ChannelMemberDto) => void
  onLeaveChannel: () => void
}

export default function ChannelDetailsDialog({
  open,
  onClose,
  channel,
  members,
  currentUser,
  activeUserIds,
  onPromote,
  onRemove,
  onLeaveChannel
}: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedMember, setSelectedMember] = useState<ChannelMemberDto | null>(null)

  const isAdmin = members.find(m => m.profile?.id === currentUser.id)?.role === "Admin"

  const isOnlyAdmin = (member: ChannelMemberDto) =>
    member?.role === "Admin" &&
    members.filter((m) => m.role === "Admin").length === 1

  const openMenu = (event: React.MouseEvent<HTMLElement>, member: ChannelMemberDto) => {
    setAnchorEl(event.currentTarget)
    setSelectedMember(member)
  }

  const closeMenu = () => {
    setAnchorEl(null)
    setSelectedMember(null)
  }

  const renderMember = (member: ChannelMemberDto) => {
    const isSelf = member.profile?.id === currentUser.id
    const isOnline = member.profile?.id !== undefined && activeUserIds.includes(member.profile.id)
    const roleLabel = member.role === "Admin" ? "Administrador" : "Miembro"

    return (
      <ListItem
        key={member.id}
        secondaryAction={
          isAdmin && !isSelf && (
            <IconButton onClick={(e) => openMenu(e, member)}>
              <MoreVert />
            </IconButton>
          )
        }
      >
        <ListItemAvatar>
          <Box position="relative">
            <Avatar src={member.profile?.avatarUrl || "/placeholder.svg"} />
            {isOnline && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  width: 12,
                  height: 12,
                  bgcolor: "green",
                  borderRadius: "50%",
                  border: "2px solid white"
                }}
              />
            )}
          </Box>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontWeight={isSelf ? 600 : 400}>
                {member.profile?.username}
                {isSelf && " (Tú)"}
              </Typography>
              {member.role === "Admin" && <AdminPanelSettings fontSize="small" color="primary" />}
            </Box>
          }
          secondary={roleLabel}
        />
      </ListItem>
    )
  }

  const onlineMembers = members.filter(
    (m) => m.profile?.id !== undefined && activeUserIds.includes(m.profile.id)
  )
  const offlineMembers = members.filter(
    (m) => m.profile?.id === undefined || !activeUserIds.includes(m.profile.id)
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Detalles del canal</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "75vh" }}>
        {/* Header */}
        <Box textAlign="center" mb={2}>
          <Avatar
            src={channel?.imageUrl || "/placeholder.svg"}
            sx={{ width: 100, height: 100, mx: "auto", mb: 1 }}
          />
          <Typography variant="h6">{channel?.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {channel?.description}
          </Typography>
          <Box mt={1} display="flex" justifyContent="center" gap={1} flexWrap="wrap">
            <Chip
              icon={channel?.isPublic ? <Public /> : <Lock />}
              label={channel?.isPublic ? "Público" : "Privado"}
              color={channel?.isPublic ? "success" : "warning"}
              variant="outlined"
              size="small"
            />
            {channel?.whatsappLink && (
              <Button
                variant="outlined"
                size="small"
                color="success"
                startIcon={<WhatsApp />}
                href={channel?.whatsappLink}
                target="_blank"
              >
                WhatsApp
              </Button>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" mt={1}>
            {channel?.createdAt
              ? `Creado ${formatDistanceToNow(new Date(channel.createdAt), { addSuffix: true, locale: es })}`
              : "Fecha de creación desconocida"}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Online members */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Conectados ({onlineMembers.length})
        </Typography>
        <List dense>{onlineMembers.map(renderMember)}</List>

        {/* Offline members */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Desconectados ({offlineMembers.length})
        </Typography>
        <List dense>{offlineMembers.map(renderMember)}</List>

        {/* Leave channel button */}
        <Divider sx={{ my: 2 }} />

        <Box display="flex" justifyContent="center">
          <Button
            variant="outlined"
            color="error"
            startIcon={<ExitToApp />}
            onClick={onLeaveChannel}
            disabled={members.length === 1}
            sx={{ ":disabled": { opacity: 0.5, cursor: "not-allowed" } }}
          >
            Dejar canal
          </Button>
        </Box>
      </DialogContent>

      {/* Admin Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        { selectedMember?.role === "Member" ? (
        <MenuItem
          onClick={() => {
            if (selectedMember) {
              onPromote(selectedMember, "Admin")
              closeMenu()
            }
          }}
        >
          <SupervisorAccount fontSize="small" sx={{ mr: 1 }} />
          Hacer Administrador
        </MenuItem>
        ): (
        <MenuItem
          onClick={() => {
            if (selectedMember) {
              onPromote(selectedMember, "Member")
              closeMenu()
            }
          }}
        >
          <PersonRemove fontSize="small" sx={{ mr: 1 }} />
          Remover como Administrador
        </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (selectedMember && !isOnlyAdmin(selectedMember)) {
              onRemove(selectedMember)
              closeMenu()
            }
          }}
          disabled={!selectedMember || isOnlyAdmin(selectedMember)}
        >
          <PersonRemove fontSize="small" sx={{ mr: 1 }} />
          Eliminar del canal
        </MenuItem>
      </Menu>
    </Dialog>
  )
}
